import { honestyOf, shownHonesty, type HonestyFlags, type HonestyLabel } from '@aithora/shared';
import { useDemoBuilding } from '../session/DemoScope';
import { Est, useBuildLabels } from './buildLabels';

const warned = new Set<string>();

/** In development a figure with no honesty flag is marked on screen and in the console. */
export const SHOW_MISSING_FLAGS: boolean = import.meta.env.DEV === true;

/**
 * MEASURED / MODELED / SAMPLE / ESTIMATED, decided only by the flags the API sent
 * (outermost response first, the figure itself last). No flag, no badge: in a
 * production build nothing is drawn; in development a dashed "NO FLAG" marker names the
 * figure so the API gets fixed rather than the UI guessing.
 */
export function HonestyBadge({
  flags,
  figure,
  className,
  hide,
}: {
  flags: (HonestyFlags | null | undefined)[];
  figure: string;
  className?: string;
  /**
   * Labels the design writes inline in the text instead of as a badge (for example
   * "last 14 days · measured"). A hidden label still counts as a flag, so it never
   * draws the NO FLAG marker; SAMPLE is never hideable here. The one place SAMPLE is
   * not drawn is a single demo building scope, where the page's banner says it.
   */
  hide?: Exclude<HonestyLabel, 'SAMPLE'>[];
}) {
  const demoBuilding = useDemoBuilding();
  const buildLabels = useBuildLabels();
  const label = honestyOf(...flags);
  // Build labels show to Developer and Super Admin only (ui/buildLabels). Everyone else
  // sees a quiet "est." after a modeled or estimated figure, and nothing for the rest
  // (Andrew 2026-10-15). `hide` is for the label words a page writes inline; a customer's
  // copy of that text drops them (useBuildText), so the "est." still shows here.
  if (!buildLabels) return label === 'MODELED' || label === 'ESTIMATED' ? <Est /> : null;
  if (label && hide?.includes(label as Exclude<HonestyLabel, 'SAMPLE'>)) return null;
  // A seeded building scope (DemoScope) shows no banner and no "demo" on screen (Andrew
  // 2026-10-09), and its figures carry no SAMPLE tag. A real building's SAMPLE
  // figure, and every figure on a fleet view, keep theirs.
  if (label && shownHonesty(label, demoBuilding) === null) return null;
  if (label) {
    return (
      <span className={`honesty honesty--${label.toLowerCase()}${className ? ` ${className}` : ''}`} data-honesty={label} title={titleFor(label)}>
        {label}
      </span>
    );
  }
  if (!SHOW_MISSING_FLAGS) return null;
  if (!warned.has(figure)) {
    warned.add(figure);
    console.warn(`[honesty] "${figure}" has no real/basis/modeled flag from the API, so no badge is drawn.`);
  }
  return (
    <span className="honesty honesty--missing" data-honesty="missing" title={`${figure}: the API sent no honesty flag`}>
      NO FLAG
    </span>
  );
}

function titleFor(l: string): string {
  switch (l) {
    case 'MEASURED':
      return 'Measured from recorded events';
    case 'MODELED':
      return 'Modeled: an estimate from a model, not a recorded figure';
    case 'SAMPLE':
      return 'Sample data, not this account';
    default:
      return 'Estimated';
  }
}
